import { CheckCircle2, Truck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MyFortAgentItem } from "@/api/types";
import { getAgentTierKey } from "@/config/common";
import { useGetUserContactProfile } from "@/hooks/api/useGetUserContactProfile";
import { C } from "./homeTheme";
import { useTranslation } from "react-i18next";

/** 接口未接：默认处于「待发货」，与 dapp16 占位一致 */
const MOCK_DELIVERY_PENDING = true;

type TimelineStep = {
  timeKey: string;
  descKey: string;
  done: boolean;
  current: boolean;
};

function buildTimelineSteps(pendingShipment: boolean): TimelineStep[] {
  return [
    {
      timeKey: "homePage.deliveryStatusDialog.stepPending",
      descKey: "homePage.deliveryStatusDialog.stepPendingDesc",
      done: true,
      current: false,
    },
    {
      timeKey: "homePage.deliveryStatusDialog.stepPacking",
      descKey: "homePage.deliveryStatusDialog.stepPackingDesc",
      done: !pendingShipment,
      current: pendingShipment,
    },
    {
      timeKey: "homePage.deliveryStatusDialog.stepShipped",
      descKey: "homePage.deliveryStatusDialog.stepShippedDesc",
      done: false,
      current: false,
    },
    {
      timeKey: "homePage.deliveryStatusDialog.stepInTransit",
      descKey: "homePage.deliveryStatusDialog.stepInTransitDesc",
      done: false,
      current: false,
    },
    {
      timeKey: "homePage.deliveryStatusDialog.stepDelivered",
      descKey: "homePage.deliveryStatusDialog.stepDeliveredDesc",
      done: false,
      current: false,
    },
  ];
}

export function DeliveryStatusDialog({
  open,
  onClose,
  agent,
}: {
  open: boolean;
  onClose: () => void;
  agent?: MyFortAgentItem | null;
}) {
  const { t } = useTranslation();
  const { data: contactProfile } = useGetUserContactProfile(open);

  const tierLabel = agent ? t(getAgentTierKey(agent.name)) : "";
  const steps = buildTimelineSteps(MOCK_DELIVERY_PENDING);

  const contactAddress = contactProfile?.contactAddress?.trim() ?? "";
  const recipientName = contactProfile?.userName?.trim() ?? "";
  const phone = contactProfile?.contact?.trim() ?? "";
  const hasShippingAddress = Boolean(contactAddress);

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="max-w-sm border-0 p-0 overflow-hidden"
        style={{ background: C.card, borderRadius: 16 }}
      >
        <div className="p-5 space-y-4">
          <DialogHeader>
            <DialogTitle
              className="text-sm font-bold text-left"
              style={{ color: C.text }}
            >
              <Truck
                size={14}
                className="inline mr-1.5 align-text-bottom"
                style={{ color: C.cyan }}
              />
              {agent
                ? t("homePage.deliveryStatusDialog.titleWithAgent", {
                    name: agent.name,
                    tier: tierLabel,
                  })
                : t("homePage.deliveryStatusDialog.title")}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-0">
              {steps.map((step, i) => (
                <div key={step.timeKey} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className="w-3 h-3 rounded-full border-2 flex-shrink-0 flex items-center justify-center"
                      style={{
                        borderColor: step.done
                          ? C.primary
                          : step.current
                            ? C.cyan
                            : C.dim,
                        background: step.done ? C.primary : "transparent",
                      }}
                    >
                      {step.done && (
                        <CheckCircle2
                          size={8}
                          className="text-white"
                          strokeWidth={3}
                        />
                      )}
                    </div>
                    {i < steps.length - 1 && (
                      <div
                        className="w-px h-8 flex-shrink-0"
                        style={{
                          background: step.done ? C.primary : C.border,
                        }}
                      />
                    )}
                  </div>
                  <div className="pb-3">
                    <div
                      className="text-[11px] font-semibold"
                      style={{
                        color: step.done || step.current ? C.text : C.dim,
                      }}
                    >
                      {t(step.timeKey)}
                    </div>
                    <div className="text-[10px]" style={{ color: C.muted }}>
                      {t(step.descKey)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {hasShippingAddress && (
              <div
                className="rounded-lg p-2.5"
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: `1px solid ${C.border}`,
                }}
              >
                <div className="text-[10px] mb-1" style={{ color: C.dim }}>
                  {t("homePage.deliveryStatusDialog.shippingAddress")}
                </div>
                {(recipientName || phone) && (
                  <div className="text-[11px] mb-0.5" style={{ color: C.text }}>
                    {[recipientName, phone].filter(Boolean).join(" · ")}
                  </div>
                )}
                <div
                  className="text-[11px] leading-relaxed"
                  style={{ color: C.text }}
                >
                  {contactAddress}
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
